/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useRef } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Owners, UserContextType } from '../../../models/User';
import { FlexBox, FlexBoxBetween } from '../../../components/tableItems';
import { MainButton } from '../../../components/buttons';
import { IS_USER_ADMIN } from '../../../constants/user';
import useWeeklyLogStore from '../../../stores/weeklyLog/WeeklyLogStore';
import AutoCompleteSelect from '../../../components/Filters/AutoCompleteSelect';
import DateFilters from '../../../components/Filters/DateFilters';
import Context from '../../../context/UserContext';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '../../../components/Accordion/Accordion';
import { filtersWeeklyLogsDefault } from '../WeeklyLogModel';

interface WeeklyLogFiltersProps {
  users: Owners[];
  closeFilterDrawer: () => void;
}

const WeeklyLogFilters: React.FC<WeeklyLogFiltersProps> = ({
  users,
  closeFilterDrawer,
}) => {
  const isFirstRender = useRef(true);
  const { UserContext } = useContext(Context) as UserContextType;
  const {
    filtersValuesChange,
    filtersValuesDefault,
    updateFiltersChange,
    updateFiltersDefault,
    loading,
    updateWeeklyParams,
  } = useWeeklyLogStore();

  const createQuery = () => {
    updateWeeklyParams({
      prev_week: 0,
      end_date:
        filtersValuesDefault.end_date.value === null
          ? undefined
          : filtersValuesDefault.end_date.value,
      start_date:
        filtersValuesDefault.start_date.value === null
          ? undefined
          : filtersValuesDefault.start_date.value,
      owner:
        filtersValuesDefault.owner.value === ''
          ? undefined
          : filtersValuesDefault.owner.value,
    });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    createQuery();
  }, [filtersValuesDefault]);

  useEffect(() => {
    return () => {
      updateFiltersChange(filtersWeeklyLogsDefault);
      updateFiltersDefault(filtersWeeklyLogsDefault);
    };
  }, []);

  const onEnterOption = () => updateFiltersDefault(filtersValuesDefault);

  const filtersArray = [
    {
      id: 1,
      title: 'Owner',
      admin: true,
      countUse:
        filtersValuesChange.owner.value !== ''
          ? filtersValuesChange.owner.value.split(',').length
          : 0,
      body: (
        <AutoCompleteSelect
          disabledInput={loading}
          options={users.map(item => {
            return { id: item.id, name: item.username };
          })}
          clearAll={false}
          onEnter={onEnterOption}
          placeholder="Owner"
          updateFiltersValue={(id: string, value: string) => {
            updateFiltersChange({ owner: { id, value } });
          }}
          value={users
            .map(item => {
              return { id: item.id, name: item.username };
            })
            .filter(item => {
              const id = item.id as number;
              return (
                filtersValuesChange.owner.value
                  .split(',')
                  .indexOf(id.toString()) !== -1
              );
            })}
          id={filtersValuesChange.owner.id}
        />
      ),
      disabled: !(users.length > 0),
    },
    {
      id: 2,
      title: 'Date Range',
      countUse:
        filtersValuesChange.start_date.value &&
        filtersValuesChange.end_date.value
          ? 2
          : filtersValuesChange.end_date.value ||
            filtersValuesChange.start_date.value
          ? 1
          : 0,
      body: (
        <DateFilters
          onEnter={onEnterOption}
          disableFuture
          minDate={new Date('2024-01-02')}
          disableInput={false}
          min_id={filtersValuesChange.start_date.id}
          min_value={filtersValuesChange.start_date.value}
          max_id={filtersValuesChange.end_date.id}
          max_value={filtersValuesChange.end_date.value}
          updateDates={(id: string, value: string | null) => {
            if (id === filtersValuesChange.start_date.id) {
              updateFiltersChange({ start_date: { id, value } });
              return;
            }
            if (id === filtersValuesChange.end_date.id) {
              updateFiltersChange({ end_date: { id, value } });
            }
          }}
        />
      ),
      disabled: false,
    },
  ];

  return (
    <Box marginTop={2}>
      <FlexBoxBetween px={3}>
        <Typography sx={{ fontWeight: 900, fontSize: 16 }}>Filters</Typography>
        <IconButton
          aria-label="close"
          disabled={false}
          id="long-button"
          aria-haspopup="true"
          onClick={() => closeFilterDrawer()}
        >
          <CloseIcon
            sx={{
              color: 'black',
              '&:hover': { color: 'red' },
            }}
          />
        </IconButton>
      </FlexBoxBetween>
      <FlexBoxBetween px={3} mt={1}>
        <MainButton
          disabled={false}
          size="small"
          variant="contained"
          type="submit"
          onClick={() => {
            updateFiltersDefault(filtersValuesChange);
            closeFilterDrawer();
          }}
        >
          Apply Filters
        </MainButton>
        <Typography
          onClick={() => {}}
          sx={{
            fontSize: 14,
            cursor: 'pointer',
            ':hover': {
              color: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          Clear All
        </Typography>
      </FlexBoxBetween>
      <Box marginTop={1.5}>
        <Divider sx={{ border: '1px solid rgba(0, 0, 0, 0.06)' }} />

        <div>
          {filtersArray.map(filter => (
            <Box
              position="relative"
              key={filter.id}
              display={
                filter.admin &&
                !IS_USER_ADMIN.includes(UserContext.profile.role)
                  ? 'none'
                  : ''
              }
            >
              <Accordion>
                <AccordionSummary
                  disabled={filter.disabled}
                  aria-controls={`${filter.title}-content`}
                  id={`${filter.title}-header`}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <FlexBox>
                      <Box
                        sx={{
                          backgroundColor:
                            filter.countUse !== undefined && filter.countUse > 0
                              ? '#CEFFBA'
                              : '',
                        }}
                      >
                        <Typography
                          textTransform="capitalize"
                          fontSize={12}
                          color="#000000"
                          fontWeight={700}
                        >
                          {filter.title}
                        </Typography>
                      </Box>
                      {filter.countUse !== undefined && filter.countUse > 0 && (
                        <Typography
                          textTransform="capitalize"
                          fontSize={12}
                          color="#000000"
                          marginLeft={1.5}
                          fontWeight={400}
                          sx={{
                            backgroundColor: '#E2E2E2',
                            padding: '2px 4px',
                            borderRadius: '2px',
                          }}
                        >
                          {filter.countUse}
                        </Typography>
                      )}
                    </FlexBox>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>{filter.body}</AccordionDetails>
              </Accordion>
            </Box>
          ))}
        </div>
      </Box>
    </Box>
  );
};

export default WeeklyLogFilters;
